/**
 * Sidecart functionality
 */

class Sidecart {
  constructor() {
    this.overlay = document.querySelector('#overlay');
    this.sidecart = document.querySelector('.sidecart');
    this.closeBtn = document.querySelector('.sidecart__close');
    this.overlayBg = document.querySelector('.overlay__background');
    this.itemsContainer = document.querySelector('#sidecart-items');

    // Get free shipping threshold from data attribute (in cents)
    this.freeShippingThreshold = parseInt(this.overlay?.dataset?.freeShippingThreshold) || 0;

    // Libellés traduisibles fournis par le Liquid (data-attributes sur .sidecart)
    const ds = this.sidecart?.dataset || {};
    this.i18n = {
      shippingUnlocked: ds.i18nShippingUnlocked || 'Livraison offerte !',
      shippingRemaining: ds.i18nShippingRemaining || 'Plus que [amount] pour profiter de la <span class="fw-7">livraison offerte</span>',
      discountChecking: ds.i18nDiscountChecking || 'Vérification...',
      discountInvalid: ds.i18nDiscountInvalid || 'Code promo invalide',
      discountApplied: ds.i18nDiscountApplied || 'Code promo appliqué avec succès !',
      discountError: ds.i18nDiscountError || "Erreur lors de l'application du code",
      discountRemoved: ds.i18nDiscountRemoved || 'Code promo retiré',
      discountRemoveError: ds.i18nDiscountRemoveError || 'Erreur lors de la suppression',
    };

    this.isUpdating = false;
    this.isHandlingGifts = false;
    this.currentCartTotal = 0;

    this.init();
  }

  init() {
    if (!this.overlay || !this.sidecart) return;

    // Event listeners
    this.setupEventListeners();

    // Initialize countdown
    this.initCountdown();

    // Update cart on load (will initialize suggested products after cart is loaded)
    this.updateCart();

    // Listen to global cart update events
    document.addEventListener('cart:updated', () => {
      this.updateCart();
    });
  }

  setupEventListeners() {
    // Close sidecart
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.close());
    }

    if (this.overlayBg) {
      this.overlayBg.addEventListener('click', () => this.close());
    }

    // Open sidecart triggers (cart icon clicks)
    document.addEventListener('click', (e) => {
      const cartTrigger = e.target.closest('[data-cart-trigger]');
      if (cartTrigger) {
        e.preventDefault();
        this.open();
      }
    });

    // Quantity buttons
    document.addEventListener('click', (e) => {
      const increaseBtn = e.target.closest('.sidecart-item__qty-increase');
      const decreaseBtn = e.target.closest('.sidecart-item__qty-decrease');

      if (increaseBtn) {
        e.preventDefault();
        const line = increaseBtn.getAttribute('data-line');
        console.log('Increase clicked, line:', line);
        if (line) {
          this.updateQuantity(line, 1);
        }
      }

      if (decreaseBtn) {
        e.preventDefault();
        const line = decreaseBtn.getAttribute('data-line');
        console.log('Decrease clicked, line:', line);
        if (line) {
          this.updateQuantity(line, -1);
        }
      }
    });

    // Remove button
    document.addEventListener('click', async (e) => {
      const removeBtn = e.target.closest('.sidecart-item__remove');
      if (removeBtn) {
        e.preventDefault();
        const line = removeBtn.getAttribute('data-line');
        console.log('Remove clicked, line:', line);
        if (line) {
          // Find the item and get current quantity
          const item = document.querySelector(`[data-line-item="${line}"]`);
          if (item) {
            const qtyValue = item.querySelector('.sidecart-item__qty-value');
            const currentQty = parseInt(qtyValue.textContent);
            // Pass negative change to set quantity to 0
            await this.updateQuantity(line, -currentQty);
          }
        }
      }
    });

    // Discount code
    const applyDiscountBtn = document.querySelector('#apply-discount');
    const discountInput = document.querySelector('#discount-code-input');

    if (applyDiscountBtn) {
      applyDiscountBtn.addEventListener('click', () => this.applyDiscount());
    }

    // Allow Enter key to apply discount
    if (discountInput) {
      discountInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.applyDiscount();
        }
      });
    }

    // Suggested products add to cart
    document.addEventListener('click', (e) => {
      const addBtn = e.target.closest('.sidecart-suggested-item__add');
      if (addBtn) {
        const variantId = addBtn.dataset.variantId;
        if (variantId) {
          this.addToCart(variantId);
        }
      }
    });

    // Subscription toggle
    document.addEventListener('change', async (e) => {
      if (e.target.classList.contains('sidecart-item__toggle-input')) {
        const checkbox = e.target;
        const item = checkbox.closest('.sidecart-item');
        const subscriptionSection = item.querySelector('.sidecart-item__subscription');
        const lineItem = parseInt(item.dataset.lineItem);

        if (checkbox.checked) {
          // Activate subscription - get first selling plan
          const sellingPlansData = subscriptionSection.dataset.sellingPlans;
          if (sellingPlansData) {
            const firstPlan = sellingPlansData.split(',')[0];
            const sellingPlanId = parseInt(firstPlan.split(':')[0]);

            await this.toggleSubscription(lineItem, sellingPlanId);
          } else {
            console.warn('No selling plans available for this product');
            checkbox.checked = false;
          }
        } else {
          // Deactivate subscription
          await this.toggleSubscription(lineItem, null);
        }
      }
    });

    // Subscription dropdown click
    document.addEventListener('click', (e) => {
      const dropdown = e.target.closest('.sidecart-item__subscription-dropdown');
      if (dropdown) {
        const menu = dropdown.nextElementSibling;
        if (menu && menu.classList.contains('sidecart-item__subscription-menu')) {
          const isOpen = menu.style.display === 'block';
          // Close all other dropdowns
          document.querySelectorAll('.sidecart-item__subscription-menu').forEach(m => {
            m.style.display = 'none';
          });
          document.querySelectorAll('.sidecart-item__subscription-dropdown').forEach(d => {
            d.classList.remove('is-open');
          });

          // Toggle current dropdown
          if (!isOpen) {
            menu.style.display = 'block';
            dropdown.classList.add('is-open');
          }
        }
      }
    });

    // Subscription dropdown option click
    document.addEventListener('click', async (e) => {
      const option = e.target.closest('.sidecart-item__subscription-option');
      if (option) {
        const menu = option.closest('.sidecart-item__subscription-menu');
        const dropdownWrapper = menu.closest('.sidecart-item__subscription-dropdown-wrapper');
        const dropdown = dropdownWrapper.querySelector('.sidecart-item__subscription-dropdown');
        const dropdownText = dropdown.querySelector('p');
        const item = option.closest('.sidecart-item');
        const lineItem = parseInt(item.dataset.lineItem);
        const sellingPlanId = parseInt(option.dataset.planId);
        const planName = option.dataset.planName;

        // Update UI
        dropdownText.textContent = planName;
        menu.style.display = 'none';
        dropdown.classList.remove('is-open');

        // Update cart with new selling plan
        await this.changeSubscriptionFrequency(lineItem, sellingPlanId);
      }
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.sidecart-item__subscription-dropdown-wrapper')) {
        document.querySelectorAll('.sidecart-item__subscription-menu').forEach(menu => {
          menu.style.display = 'none';
        });
        document.querySelectorAll('.sidecart-item__subscription-dropdown').forEach(dropdown => {
          dropdown.classList.remove('is-open');
        });
      }
    });

    // Escape key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.overlay.classList.contains('is-open')) {
        this.close();
      }
    });
  }

  open() {
    this.overlay.classList.add('is-open');
    document.body.classList.add('cart-open');
    this.updateCart();
  }

  close() {
    this.overlay.classList.remove('is-open');
    document.body.classList.remove('cart-open');
  }

  async updateCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      await this.renderCart(cart);
      this.updateFreeShippingProgress(cart.total_price);
      this.updateCartCount(cart.item_count);
      this.updateCheckoutButton(cart);
      this.toggleEmptyStateUI(cart.item_count === 0);

      // Handle gift auto-add/remove
      await this.handleGifts(cart);

      // Check if there's an active discount code
      if (cart.discount_codes && cart.discount_codes.length > 0) {
        const activeDiscount = cart.discount_codes.find(d => d.applicable);
        if (activeDiscount) {
          this.displayActiveDiscount(activeDiscount.code);
        } else {
          this.hideActiveDiscount();
        }
      } else {
        this.hideActiveDiscount();
      }
    } catch (error) {
      console.error('Error updating cart:', error);
    }
  }

  toggleEmptyStateUI(isEmpty) {
    console.log('toggleEmptyStateUI called with isEmpty:', isEmpty);
    // Hide flash offer, suggested products, and footer container when cart is empty
    const emptyState = document.querySelector('.sidecart__empty');
    const flashOffer = document.querySelector('.sidecart__flash-offer');
    const suggestedProducts = document.querySelector('.sidecart__suggested');
    const footerContainer = document.querySelector('.sidecart__footer__container');

    console.log('Found elements:', { emptyState, flashOffer, suggestedProducts, footerContainer });

    // Show empty state when cart is empty
    if (emptyState) {
      emptyState.style.display = isEmpty ? 'flex' : 'none';
      console.log('Empty state display set to:', emptyState.style.display);
    }

    if (flashOffer) {
      flashOffer.style.display = isEmpty ? 'none' : 'flex';
      console.log('Flash offer display set to:', flashOffer.style.display);
    }
    if (suggestedProducts) {
      suggestedProducts.style.display = isEmpty ? 'none' : 'flex';
      console.log('Suggested products display set to:', suggestedProducts.style.display);
    }

    if (footerContainer) {
      footerContainer.style.display = isEmpty ? 'none' : 'flex';
      console.log('Footer container display set to:', footerContainer.style.display);
    }
  }

  async renderCart(cart) {
    // Use Shopify Section Rendering API to re-render from server
    // This preserves all Liquid logic for selling plans
    try {
      console.log('Sidecart - Rendering cart with items:', cart.items);

      // Log properties for each item
      if (cart.items) {
        cart.items.forEach((item, index) => {
          console.log(`Sidecart - Item ${index}:`, {
            title: item.title,
            properties: item.properties
          });
        });
      }

      // Use the full section ID for grouped sections
      const sectionId = this.overlay?.dataset?.sectionId || 'sidecart';
      // Utilise la racine localisée (Shopify.routes.root = "/", "/de/", etc.) pour que
      // le Section Rendering renvoie le sidecart dans la langue/marché courant (sinon FR par défaut)
      const localizedRoot = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${localizedRoot}cart?section_id=${sectionId}`);
      const sidecartHTML = await response.text();

      // Only process if we got valid HTML back
      if (!sidecartHTML || sidecartHTML.trim().length === 0) return;

      const parser = new DOMParser();
      const doc = parser.parseFromString(sidecartHTML, 'text/html');

      const newItemsWrapper = doc.querySelector('.sidecart-items-wrapper');
      const currentItemsWrapper = document.querySelector('.sidecart-items-wrapper');

      if (newItemsWrapper && currentItemsWrapper) {
        currentItemsWrapper.innerHTML = newItemsWrapper.innerHTML;
      }

      // Update gift section
      const newGifts = doc.querySelector('.sidecart__gifts');
      const currentGifts = document.querySelector('.sidecart__gifts');
      if (newGifts) {
        if (currentGifts) {
          currentGifts.outerHTML = newGifts.outerHTML;
        } else if (currentItemsWrapper) {
          currentItemsWrapper.insertAdjacentHTML('afterend', newGifts.outerHTML);
        }
      } else if (currentGifts && newItemsWrapper) {
        // Only remove gift section if server explicitly excluded it
        currentGifts.remove();
      }

      // Update suggested products visibility based on cart - only if cart is not empty
      if (cart.items && cart.items.length > 0) {
        this.updateSuggestedProducts(cart);
      }
    } catch (error) {
      console.error('Error rendering cart from server:', error);
    }
  }

  setLoadingState(isLoading) {
    this.isUpdating = isLoading;

    // Disable all quantity buttons
    const qtyButtons = document.querySelectorAll('.sidecart-item__qty-btn');
    qtyButtons.forEach(btn => {
      btn.disabled = isLoading;
      btn.style.opacity = isLoading ? '0.5' : '1';
      btn.style.cursor = isLoading ? 'not-allowed' : 'pointer';
    });

    // Disable add to cart buttons for suggested products
    const addButtons = document.querySelectorAll('.sidecart-suggested-item__add');
    addButtons.forEach(btn => {
      btn.disabled = isLoading;
      btn.style.opacity = isLoading ? '0.5' : '1';
      btn.style.cursor = isLoading ? 'not-allowed' : 'pointer';
    });
  }

  async updateQuantity(line, change) {
    if (this.isUpdating) {
      console.log('Update already in progress, skipping');
      return;
    }

    console.log('updateQuantity called with line:', line, 'change:', change);
    const lineNumber = parseInt(line);

    if (isNaN(lineNumber)) {
      console.error('Invalid line number:', line);
      return;
    }

    const item = document.querySelector(`[data-line-item="${lineNumber}"]`);
    console.log('Found item:', item);

    if (!item) {
      console.error('Item not found for line:', lineNumber);
      return;
    }

    const qtyValue = item.querySelector('.sidecart-item__qty-value');
    const currentQty = parseInt(qtyValue.textContent);
    const newQty = Math.max(0, currentQty + change);

    console.log('Updating quantity from', currentQty, 'to', newQty);

    this.setLoadingState(true);

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line: lineNumber,
          quantity: newQty
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Cart update failed:', errorText);
        throw new Error('Failed to update cart');
      }

      const cart = await response.json();
      console.log('Cart updated successfully:', cart);

      await this.renderCart(cart);
      this.updateFreeShippingProgress(cart.total_price);
      this.updateCartCount(cart.item_count);
      this.updateCheckoutButton(cart);
      this.toggleEmptyStateUI(cart.item_count === 0);
      await this.handleGifts(cart);
    } catch (error) {
      console.error('Error updating quantity:', error);
    } finally {
      this.setLoadingState(false);
    }
  }

  async toggleSubscription(lineItem, sellingPlanId) {
    if (this.isUpdating) {
      console.log('Update already in progress, skipping subscription toggle');
      return;
    }

    this.setLoadingState(true);

    try {
      const item = document.querySelector(`[data-line-item="${lineItem}"]`);
      if (!item) {
        console.error('Item not found for line:', lineItem);
        return;
      }

      const qtyValue = item.querySelector('.sidecart-item__qty-value');
      const currentQty = parseInt(qtyValue.textContent);

      // IMPORTANT: Store variant ID BEFORE any DOM manipulation
      const variantId = item.dataset.variantId;
      console.log('Stored variant ID:', variantId, 'from dataset:', item.dataset);

      // Build request body
      // Pour RETIRER un selling plan, on doit d'abord retirer l'item puis le rajouter sans selling_plan
      // Mais /cart/change.js ne supporte pas de retirer un selling plan directement

      if (sellingPlanId === null) {
        // Pour retirer l'abonnement : on doit remove puis re-add l'item
        console.log('Removing subscription - will remove then re-add item');

        if (!variantId) {
          console.error('Cannot find variant ID to re-add item');
          console.error('Item element:', item);
          console.error('Available data attributes:', Object.keys(item.dataset));
          throw new Error('Variant ID not found');
        }

        // D'abord, on retire l'item
        const removeResponse = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            line: lineItem,
            quantity: 0
          })
        });

        if (!removeResponse.ok) {
          throw new Error('Failed to remove item');
        }

        // Ensuite on le rajoute sans selling plan
        console.log('Re-adding item with variant ID:', variantId);

        const addResponse = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: variantId,
            quantity: currentQty
          })
        });

        if (!addResponse.ok) {
          throw new Error('Failed to re-add item without subscription');
        }

        await addResponse.json();
        console.log('Item re-added without subscription');

        // Get the full cart since /cart/add.js only returns the added item
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();
        console.log('Full cart after removing subscription - total_price:', cart.total_price, 'original_total_price:', cart.original_total_price);

        // Re-render cart from server
        await this.renderCart(cart);
        this.updateFreeShippingProgress(cart.total_price);
        this.updateCartCount(cart.item_count);
        this.updateCheckoutButton(cart);
        await this.handleGifts(cart);

        this.setLoadingState(false);
        return;
      }

      // Pour AJOUTER ou CHANGER un selling plan
      const body = {
        line: lineItem,
        quantity: currentQty,
        selling_plan: sellingPlanId
      };

      console.log('Toggling subscription with body:', body);

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Subscription toggle failed:', errorText);
        throw new Error('Failed to toggle subscription');
      }

      const cart = await response.json();
      console.log('Subscription toggled successfully:', cart);
      console.log('Cart total_price:', cart.total_price, 'Cart original_total_price:', cart.original_total_price);

      // Re-render cart from server (Liquid will handle the correct state)
      await this.renderCart(cart);
      this.updateFreeShippingProgress(cart.total_price);
      this.updateCartCount(cart.item_count);
      this.updateCheckoutButton(cart);
      await this.handleGifts(cart);
    } catch (error) {
      console.error('Error toggling subscription:', error);
      // Revert checkbox state on error
      const itemElement = document.querySelector(`[data-line-item="${lineItem}"]`);
      const checkbox = itemElement?.querySelector('.sidecart-item__toggle-input');
      if (checkbox) {
        checkbox.checked = !checkbox.checked;
      }
    } finally {
      this.setLoadingState(false);
    }
  }

  async changeSubscriptionFrequency(lineItem, sellingPlanId) {
    if (this.isUpdating) {
      console.log('Update already in progress, skipping frequency change');
      return;
    }

    this.setLoadingState(true);

    try {
      const item = document.querySelector(`[data-line-item="${lineItem}"]`);
      if (!item) {
        console.error('Item not found for line:', lineItem);
        return;
      }

      const qtyValue = item.querySelector('.sidecart-item__qty-value');
      const currentQty = parseInt(qtyValue.textContent);

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line: lineItem,
          quantity: currentQty,
          selling_plan: sellingPlanId
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Frequency change failed:', errorText);
        throw new Error('Failed to change subscription frequency');
      }

      const cart = await response.json();
      console.log('Subscription frequency changed successfully:', cart);

      await this.renderCart(cart);
      this.updateFreeShippingProgress(cart.total_price);
      this.updateCartCount(cart.item_count);
      this.updateCheckoutButton(cart);
      await this.handleGifts(cart);
    } catch (error) {
      console.error('Error changing subscription frequency:', error);
    } finally {
      this.setLoadingState(false);
    }
  }

  async addToCart(variantId, quantity = 1) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity
        })
      });

      if (response.ok) {
        await this.updateCart();
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
    }
  }

  async handleGifts(cart) {
    if (this.isHandlingGifts) return;

    const giftDataEl = document.getElementById('sidecart-gift-data');
    if (!giftDataEl) return;

    let giftBlocks;
    try {
      giftBlocks = JSON.parse(giftDataEl.textContent || '[]');
    } catch (e) {
      return;
    }
    if (giftBlocks.length === 0) return;

    // Calculate cart total excluding gift items
    let cartTotalNoGifts = 0;
    let nonGiftCount = 0;
    for (const item of cart.items) {
      const isGift = item.properties && item.properties._is_gift === 'true';
      if (!isGift) {
        cartTotalNoGifts += item.final_line_price;
        nonGiftCount++;
      }
    }

    let cartChanged = false;
    this.isHandlingGifts = true;

    try {
      for (const gift of giftBlocks) {
        const thresholdMet = nonGiftCount > 0 && cartTotalNoGifts >= gift.amountMin;
        const giftInCart = cart.items.some(item =>
          item.variant_id === gift.variantId &&
          item.properties && item.properties._is_gift === 'true'
        );

        if (thresholdMet && !giftInCart) {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: gift.variantId,
              quantity: 1,
              properties: { _is_gift: 'true' }
            })
          });
          cartChanged = true;
        } else if (!thresholdMet && giftInCart) {
          const lineIndex = cart.items.findIndex(item =>
            item.variant_id === gift.variantId &&
            item.properties && item.properties._is_gift === 'true'
          );
          if (lineIndex >= 0) {
            await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ line: lineIndex + 1, quantity: 0 })
            });
            cartChanged = true;
          }
        }
      }

      if (cartChanged) {
        const response = await fetch('/cart.js');
        const updatedCart = await response.json();
        await this.renderCart(updatedCart);
        this.updateFreeShippingProgress(updatedCart.total_price);
        this.updateCartCount(updatedCart.item_count);
        this.updateCheckoutButton(updatedCart);
        this.toggleEmptyStateUI(updatedCart.item_count === 0);
      }
    } finally {
      this.isHandlingGifts = false;
    }
  }

  async applyDiscount() {
    const input = document.querySelector('#discount-code-input');
    const button = document.querySelector('#apply-discount');

    if (!input || !input.value.trim()) return;

    const code = input.value.trim();

    // Disable button during request
    if (button) {
      button.disabled = true;
      button.textContent = this.i18n.discountChecking;
    }

    try {


      // If code exists (200 OK), apply it to the cart
      const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          discount: code
        })
      });

      if (!response.ok) {
        throw new Error('Failed to apply discount');
      }

      const cart = await response.json();

      let discountOK = false;

      for (const discount of cart.discount_codes) {
        if (discount.code == code && discount.applicable) {
          discountOK = true;
        }
      }

      if (!discountOK) {
        this.showDiscountMessage(this.i18n.discountInvalid, 'error');
        if (button) {
          button.disabled = false;
          button.textContent = document.querySelector('[data-discount-button-text]')?.dataset.discountButtonText || 'Appliquer';
        }
        return;
      }

      // Show success message
      this.showDiscountMessage(this.i18n.discountApplied, 'success');

      // Clear input
      input.value = '';

      // Update cart display
      await this.renderCart(cart);
      this.updateFreeShippingProgress(cart.total_price);
      this.updateCartCount(cart.item_count);
      this.updateCheckoutButton(cart);
      await this.handleGifts(cart);

      // Display active discount code
      this.displayActiveDiscount(code);

    } catch (error) {
      console.error('Error applying discount:', error);
      this.showDiscountMessage(this.i18n.discountError, 'error');
    } finally {
      // Re-enable button
      if (button) {
        button.disabled = false;
        button.textContent = document.querySelector('[data-discount-button-text]')?.dataset.discountButtonText || 'Appliquer';
      }
    }
  }

  showDiscountMessage(message, type) {
    let messageContainer = document.querySelector('.sidecart__discount-message');

    // Create message container if it doesn't exist
    if (!messageContainer) {
      messageContainer = document.createElement('div');
      messageContainer.className = 'sidecart__discount-message';
      const discountSection = document.querySelector('.sidecart__discount');
      if (discountSection) {
        discountSection.parentNode.insertBefore(messageContainer, discountSection.nextSibling);
      }
    }

    messageContainer.textContent = message;
    messageContainer.className = `sidecart__discount-message sidecart__discount-message--${type}`;
    messageContainer.style.display = 'block';

    // Hide message after 3 seconds
    setTimeout(() => {
      messageContainer.style.display = 'none';
    }, 3000);
  }

  hideActiveDiscount() {
    const activeDiscountEl = document.querySelector('.sidecart__active-discount');
    if (activeDiscountEl) {
      activeDiscountEl.style.display = 'none';
    }
  }

  displayActiveDiscount(code) {
    let activeDiscountEl = document.querySelector('.sidecart__active-discount');

    // Create active discount display if it doesn't exist
    if (!activeDiscountEl) {
      activeDiscountEl = document.createElement('div');
      activeDiscountEl.className = 'sidecart__active-discount';
      const discountSection = document.querySelector('.sidecart__discount');
      if (discountSection) {
        discountSection.parentNode.insertBefore(activeDiscountEl, discountSection);
      }
    }

    activeDiscountEl.innerHTML = `
      <div class="sidecart__active-discount-content">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="#2B2B2B" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span class="p-small">Code promo : <strong>${code}</strong></span>
        <button type="button" class="sidecart__remove-discount" aria-label="Remove discount">
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0.317029 0.315389C0.738139 -0.105102 1.42074 -0.105157 1.84182 0.315389L5.00632 3.47591L8.15818 0.32861C8.57929 -0.0918928 9.26249 -0.0919375 9.68357 0.32861C10.1047 0.74916 10.1046 1.4315 9.68357 1.85208L6.53171 4.99938L9.68417 8.14788C10.1053 8.56845 10.1053 9.25078 9.68417 9.67135C9.2631 10.0917 8.58043 10.0917 8.15939 9.67135L5.00632 6.52225L1.84061 9.68457C1.41951 10.1051 0.736929 10.1051 0.315826 9.68457C-0.105275 9.264 -0.105276 8.58227 0.315826 8.1617L3.48153 4.99878L0.317029 1.83826C-0.104074 1.41768 -0.104074 0.735962 0.317029 0.315389Z" fill="#2B2B2B" fill-opacity="0.5"/>
          </svg>
        </button>
      </div>
    `;
    activeDiscountEl.style.display = 'flex';

    // Add event listener for remove button
    const removeBtn = activeDiscountEl.querySelector('.sidecart__remove-discount');
    if (removeBtn) {
      removeBtn.addEventListener('click', () => this.removeDiscount());
    }
  }

  async removeDiscount() {
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          discount: ''
        })
      });

      if (!response.ok) {
        throw new Error('Failed to remove discount');
      }

      const cart = await response.json();

      // Hide active discount display
      this.hideActiveDiscount();

      // Update cart display
      await this.renderCart(cart);
      this.updateFreeShippingProgress(cart.total_price);
      this.updateCartCount(cart.item_count);
      this.updateCheckoutButton(cart);
      await this.handleGifts(cart);

      this.showDiscountMessage(this.i18n.discountRemoved, 'success');

    } catch (error) {
      console.error('Error removing discount:', error);
      this.showDiscountMessage(this.i18n.discountRemoveError, 'error');
    }
  }

  updateFreeShippingProgress(cartTotal) {
    const progressBar = document.querySelector('.sidecart__shipping-fill');
    const message = document.querySelector('.sidecart__shipping-message p');

    if (!progressBar || !message) return;
    if (!this.freeShippingThreshold) return;

    // Debug log
    console.log('updateFreeShippingProgress called with cartTotal:', cartTotal, 'type:', typeof cartTotal);

    // Ensure cartTotal is a valid number
    const validCartTotal = Number(cartTotal) || 0;

    console.log('validCartTotal after Number():', validCartTotal);

    const remaining = Math.max(0, this.freeShippingThreshold - validCartTotal);
    const newPercentage = Math.min(100, (validCartTotal / this.freeShippingThreshold) * 100);

    // Force reflow to ensure transition works
    progressBar.style.transition = 'none';
    void progressBar.offsetWidth; // Force reflow

    // Animate progress bar from old to new value
    progressBar.style.transition = 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)';
    progressBar.style.width = `${newPercentage}%`;

    // Store current total for next animation
    this.currentCartTotal = validCartTotal;

    // Update message HTML with correct amount (libellés traduisibles)
    if (remaining === 0) {
      message.innerHTML = `<span class="fw-7">${this.i18n.shippingUnlocked}</span>`;
    } else {
      const amountHtml = `<span class="sidecart__shipping-amount fw-7">${this.formatMoney(remaining)}</span>`;
      message.innerHTML = this.i18n.shippingRemaining.replace('[amount]', amountHtml);
    }
  }

  updateCartCount(count) {
    // Update sidecart header count
    const sidecartCounts = document.querySelectorAll('.sidecart__cart-count');
    sidecartCounts.forEach(el => {
      el.textContent = count;
    });

    // Update header cart count (desktop)
    const headerCartCounts = document.querySelectorAll('.header-menu__cart-count');
    headerCartCounts.forEach(el => {
      el.textContent = count;
    });

    // Update header cart count (mobile)
    const mobileCartCounts = document.querySelectorAll('.header-menu__mobile-cart-count');
    mobileCartCounts.forEach(el => {
      el.textContent = count;
    });

    // Update data-cart-count attributes (for accessibility)
    const cartButtons = document.querySelectorAll('[data-cart-count]');
    cartButtons.forEach(btn => {
      btn.setAttribute('data-cart-count', count);
    });
  }

  async updateCheckoutButton(cart) {
    const ctaBtn = document.querySelector('.sidecart__cta');
    if (!ctaBtn) return;

    const pricesContainer = ctaBtn.querySelector('.sidecart__cta-prices');
    if (!pricesContainer) return;

    // Calculate total compare_at_price by fetching product data
    let totalCompareAtPrice = 0;

    console.log('=== updateCheckoutButton Debug ===');
    console.log('Cart total_price:', cart.total_price);
    console.log('Cart original_total_price:', cart.original_total_price);
    console.log('Cart items_subtotal_price:', cart.items_subtotal_price);

    for (const item of cart.items) {
      try {
        console.log(`\nFetching product: ${item.handle}, variant_id: ${item.variant_id}`);

        // Fetch product data to get compare_at_price
        const productResponse = await fetch(`/products/${item.handle}.js`);
        const productData = await productResponse.json();

        // Find the matching variant
        const variant = productData.variants.find(v => v.id === item.variant_id);

        console.log('Variant found:', variant ? `ID ${variant.id}` : 'NOT FOUND');
        console.log('Variant price:', variant?.price);
        console.log('Variant compare_at_price:', variant?.compare_at_price);

        if (variant && variant.compare_at_price && variant.compare_at_price > variant.price) {
          const itemTotal = variant.compare_at_price * item.quantity;
          totalCompareAtPrice += itemTotal;
          console.log(`Adding compare_at_price: ${variant.compare_at_price} x ${item.quantity} = ${itemTotal}`);
        } else {
          const itemTotal = item.price * item.quantity;
          totalCompareAtPrice += itemTotal;
          console.log(`Adding regular price: ${item.price} x ${item.quantity} = ${itemTotal}`);
        }
      } catch (error) {
        console.error('Error fetching product data:', error);
        totalCompareAtPrice += item.price * item.quantity;
      }
    }

    console.log('\nTotal compare_at_price calculated:', totalCompareAtPrice);

    // Determine which price to show as "old"
    let oldPrice = null;
    if (totalCompareAtPrice > cart.total_price) {
      oldPrice = totalCompareAtPrice;
      console.log('Using totalCompareAtPrice as oldPrice:', oldPrice);
    } else if (cart.total_price < cart.original_total_price) {
      oldPrice = cart.original_total_price;
      console.log('Using original_total_price as oldPrice:', oldPrice);
    } else {
      console.log('No oldPrice to display');
    }

    console.log('=== End Debug ===\n');

    pricesContainer.innerHTML = `
      ${oldPrice ?
        `<span class="sidecart__cta-price-old">${this.formatMoney(oldPrice)}</span>` :
        ''
      }
      <span class="sidecart__cta-price-current">${this.formatMoney(cart.total_price)}</span>
    `;

    // Update checkout URL with discount code if present
    if (cart.discount_codes && cart.discount_codes.length > 0) {
      const activeDiscount = cart.discount_codes.find(d => d.applicable);
      if (activeDiscount) {
        ctaBtn.href = `/checkout?discount=${encodeURIComponent(activeDiscount.code)}`;
      } else {
        ctaBtn.href = '/checkout';
      }
    } else {
      ctaBtn.href = '/checkout';
    }
  }

  updateSuggestedProducts(cart) {
    const suggestedSection = document.querySelector('.sidecart__suggested');
    if (!suggestedSection) {
      console.log('No suggested section found');
      return;
    }

    // Hide suggestions if cart is empty
    if (!cart.items || cart.items.length === 0) {
      console.log('Cart is empty, hiding suggested products');
      suggestedSection.style.display = 'none';
      return;
    }

    // Get all product IDs in cart
    const cartProductIds = cart.items.map(item => item.product_id);
    console.log('Cart product IDs:', cartProductIds);

    // Get all slides
    const slides = suggestedSection.querySelectorAll('.swiper-slide');
    let visibleCount = 0;

    slides.forEach(slide => {
      const productId = parseInt(slide.dataset.productId);

      if (cartProductIds.includes(productId)) {
        // Hide products in cart
        slide.classList.add('hidden');
        console.log('Hiding product:', productId);
      } else {
        // Show products not in cart
        slide.classList.remove('hidden');
        visibleCount++;
        console.log('Showing product:', productId);
      }
    });

    console.log('Visible products:', visibleCount);

    // Show/hide section
    if (visibleCount === 0) {
      suggestedSection.style.display = 'none';
    } else {
      suggestedSection.style.display = 'flex';
      // Initialize swiper
      this.initSuggestedProductsSlider();
    }
  }

  initSuggestedProductsSlider() {
    const swiperEl = document.querySelector('.sidecart-swiper');
    if (!swiperEl || typeof window.Swiper === 'undefined') return;

    // Destroy existing swiper instance if it exists
    if (swiperEl.swiper) {
      swiperEl.swiper.destroy(true, true);
    }

    new window.Swiper('.sidecart-swiper', {
      slidesPerView: 'auto',
      spaceBetween: 6,
      pagination: {
        el: '.sidecart__suggested-pagination',
        clickable: true,
      }
    });
  }

  initCountdown() {
    const flashOffer = document.querySelector('.sidecart__flash-offer');
    if (!flashOffer) return;

    const hTens = flashOffer.querySelector('[data-countdown-h-tens]');
    const hOnes = flashOffer.querySelector('[data-countdown-h-ones]');
    const mTens = flashOffer.querySelector('[data-countdown-m-tens]');
    const mOnes = flashOffer.querySelector('[data-countdown-m-ones]');
    const sTens = flashOffer.querySelector('[data-countdown-s-tens]');
    const sOnes = flashOffer.querySelector('[data-countdown-s-ones]');

    if (!mTens) return;

    const endDateStr = flashOffer.dataset.countdownEnd;
    let useEndDate = false;
    let endTime = 0;

    if (endDateStr) {
      useEndDate = true;
      endTime = new Date(endDateStr).getTime();
      if (isNaN(endTime)) return;
    }

    let totalSeconds;
    if (!useEndDate) {
      const hours = parseInt(flashOffer.dataset.countdownHours) || 0;
      const minutes = parseInt(flashOffer.dataset.countdownMinutes) || 24;
      const seconds = parseInt(flashOffer.dataset.countdownSeconds) || 31;
      totalSeconds = (hours * 3600) + (minutes * 60) + seconds;
    }

    const updateCountdown = () => {
      let remaining;
      if (useEndDate) {
        remaining = Math.floor((endTime - Date.now()) / 1000);
      } else {
        remaining = totalSeconds;
        totalSeconds--;
      }

      if (remaining <= 0) {
        if (this.countdownInterval) clearInterval(this.countdownInterval);
        flashOffer.style.display = 'none';
        return;
      }

      const hrs = Math.floor(remaining / 3600);
      const mins = Math.floor((remaining % 3600) / 60);
      const secs = remaining % 60;

      if (hTens) hTens.textContent = Math.floor(hrs / 10);
      if (hOnes) hOnes.textContent = hrs % 10;
      if (mTens) mTens.textContent = Math.floor(mins / 10);
      if (mOnes) mOnes.textContent = mins % 10;
      if (sTens) sTens.textContent = Math.floor(secs / 10);
      if (sOnes) sOnes.textContent = secs % 10;
    };

    updateCountdown();
    this.countdownInterval = setInterval(updateCountdown, 1000);
  }

  formatMoney(cents) {
    const amount = (Number(cents) || 0) / 100;
    const currency = window.Shopify?.currency?.active || 'EUR';
    const locale = (window.Shopify?.locale || 'fr').replace('_', '-');
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
  }

}

// Add CSS animations
const style = document.createElement('style');
style.textContent = `
  @keyframes slideInRight {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }

  @keyframes slideOutRight {
    from {
      transform: translateX(0);
      opacity: 1;
    }
    to {
      transform: translateX(100%);
      opacity: 0;
    }
  }
`;
document.head.appendChild(style);

// Initialize sidecart when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new Sidecart();
  });
} else {
  new Sidecart();
}